import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { Compass01Icon } from '@hugeicons/core-free-icons';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getWelcomeTour } from '@/constants/tours/tours';
import { markWelcomeSeen } from '@/lib/tour-progress';
import { startTour } from '@/redux/slices/tourSlice';

const OPEN_DELAY = 700; // let the dashboard settle first

/**
 * First sign-in on this browser: offer the welcome tour once, on the dashboard. Either answer
 * counts as seen; Help → Guided tours can always start it again.
 */
export default function TourWelcome({ tours, progress, userKey, ready }) {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const firstName = useSelector(state => state.user.data?.firstname);
  const activeTourId = useSelector(state => state.tour.activeTourId);
  const [open, setOpen] = React.useState(false);
  const welcomeTour = getWelcomeTour(tours);

  const due = ready && !progress.welcomeSeen && pathname === '/' && !activeTourId && welcomeTour;

  React.useEffect(() => {
    if (!due) return undefined;
    const timer = setTimeout(() => setOpen(true), OPEN_DELAY);
    return () => clearTimeout(timer);
  }, [due]);

  const close = () => {
    setOpen(false);
    markWelcomeSeen(userKey);
  };

  const start = () => {
    close();
    dispatch(startTour(welcomeTour.id));
  };

  return (
    <Dialog open={open} onOpenChange={next => !next && close()}>
      <DialogContent className="max-w-[420px]! gap-5" showCloseButton={false}>
        <DialogHeader className="gap-3 text-start">
          <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <HugeiconsIcon icon={Compass01Icon} strokeWidth={2} className="size-5" />
          </div>
          <DialogTitle className="text-[17px] leading-tight font-semibold tracking-[-0.01em]">
            {firstName ? `Welcome, ${firstName}` : 'Welcome to your workspace'}
          </DialogTitle>
          <DialogDescription className="text-[13.5px] leading-relaxed text-pretty">
            Take a one-minute tour of where everything lives, or explore on your own. You can take
            any tour again from Help.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>
            Not now
          </Button>
          <Button type="button" onClick={start} autoFocus>
            Take the tour
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
